import React, { useEffect, useState } from 'react';
import { connect } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { useSnackbar } from 'notistack';
import { makeStyles } from '@material-ui/core/styles';
import ShoppingCartIcon from '@material-ui/icons/ShoppingCart';
import Image from 'material-ui-image';
import { IProduct } from '../types';
import { IOrderProduct } from '../types/orderProduct';
import { upsertProductAction } from '../redux/actions';
import { upsertDirectSaleProductAction } from '../redux/actions/directSaleCart.actions';
import { ShopMode } from '../redux/types';
import AddProductModal from './AddProductModal';
import { PriceDisplay } from './PriceDisplay';
import { ImageListItem, Typography } from '@material-ui/core';
import Box from '@material-ui/core/Box';
import ButtonBase from '@material-ui/core/ButtonBase';

const useStyles = makeStyles((theme) => ({
  tileBox: {
    backgroundColor: '#fff',
    color: theme.palette.primary.main,
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'start',
    height: '100%',
  },
  image: {
    height: '100%',
    objectFit: 'contain',
    position: 'relative',
    width: '100%',
  },
  name: {
    textTransform: 'uppercase',
    minHeight: '4.5em',
    fontWeight: 600,
    margin: `${theme.spacing(2)}px ${theme.spacing(1)}px ${theme.spacing(1)}px`,
  },
  description: {
    whiteSpace: 'pre-line',
    fontWeight: 500,
    margin: `${theme.spacing(2)}px ${theme.spacing(1)}% ${theme.spacing(1)}px`,
  },
  price: {
    fontWeight: 'bold',
  },
  priceContainer: {
    display: 'flex',
    justifyContent: 'center',
    marginBottom: theme.spacing(1),
  },
  cartBox: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: `${theme.spacing(2)}px 0`,
    width: '100%',
    marginTop: 'auto',
    '& .MuiTypography-root': {
      marginRight: theme.spacing(1),
    },
  },
}));

interface OwnProps {
  product: IProduct;
  showPrice: boolean;
  shopMode?: ShopMode | null;
  onProductAdd?: (orderProduct: IOrderProduct) => void;
  linkMode?: boolean;
  id?: string;
  autoOpenModal?: boolean;
}

interface DispatchProps {
  upsertProductAction?: typeof upsertProductAction;
  upsertDirectSaleProductAction?: typeof upsertDirectSaleProductAction;
}

type Props = OwnProps & DispatchProps;

const ProductComponent: React.FC<Props> = ({
  product,
  showPrice,
  shopMode,
  upsertProductAction,
  upsertDirectSaleProductAction,
  onProductAdd,
  linkMode,
  id,
  autoOpenModal,
  ...other
}) => {
  const { enqueueSnackbar } = useSnackbar();
  const navigate = useNavigate();
  const classes = useStyles();

  const [addingProduct, setAddingProduct] = useState<IProduct | null>(null);

  useEffect(() => {
    if (autoOpenModal) {
      setAddingProduct(product);
    }
  }, [autoOpenModal, product]);

  const modalCanceled = () => {
    setAddingProduct(null);
  };

  const modalConfirmed = (orderProduct: IOrderProduct) => {
    if (onProductAdd) {
      onProductAdd(orderProduct);
    } else {
      if (shopMode === ShopMode.DirectSale && upsertDirectSaleProductAction) {
        upsertDirectSaleProductAction(orderProduct);
      } else if (upsertProductAction) {
        upsertProductAction(orderProduct);
      }
    }
    setAddingProduct(null);
    enqueueSnackbar('Prodotto aggiunto al carrello!', { variant: 'success' });
  };

  return (
    <ImageListItem id={id} {...other}>
      <Box className={classes.tileBox}>
        <Image
          className={classes.image}
          src={product.image ? product.image.url : ''}
          imageStyle={{ objectFit: 'cover' }}
        />
        <Typography className={classes.name} align={'center'}>
          {product.name}
        </Typography>
        {product.description && (
          <Typography className={classes.description} align={'left'}>
            {product.description}
          </Typography>
        )}
        {showPrice && (
          <Box className={classes.priceContainer}>
            <PriceDisplay
              price={product.price}
              discountedPrice={product.discountedPrice}
              shopMode={shopMode || ShopMode.LocalDelivery}
              variant="default"
            />
            <Typography style={{ marginLeft: 4 }}>/ {product.unit}</Typography>
          </Box>
        )}
        <ButtonBase
          className={classes.cartBox}
          onClick={() => (linkMode ? navigate(`/ordina#product-${product.id}`) : setAddingProduct(product))}
        >
          <Typography color="secondary">{linkMode ? 'Vedi prodotti' : 'Aggiungi al carrello'}</Typography>
          {!linkMode && <ShoppingCartIcon color="secondary" fontSize="small" />}
        </ButtonBase>
      </Box>
      {addingProduct && (
        <AddProductModal
          product={product}
          isUserLogged={showPrice}
          shopMode={shopMode}
          onAdd={modalConfirmed}
          onCancel={() => modalCanceled()}
        />
      )}
    </ImageListItem>
  );
};
export default connect(null, { upsertProductAction, upsertDirectSaleProductAction })(ProductComponent);
